import { Box, Breadcrumb } from '@cowsquare/design';
import { Link } from 'gatsby';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React, { useCallback } from 'react';
import { Query } from 'react-apollo';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { composeGoogleMapUrl } from '../../../utils/composeGoogleMapUrl';
import { BreadcrumbWrapper } from '../../BreadcrumbWrapper';
import Map from '../../Map.js';
import { Field, RowLink, RowMap, RowTitle } from '../../MedicalContent.js';
import { Section } from '../../Section';
import { Title } from '../../Title';
import { HTMLContent } from '../../HTMLContent';
import PageNotFound from '../error/404';

const Logo = styled(Box).attrs({
  width: ['72px', '152px'],
  height: ['72px', '152px'],
})`
  background-image: url("${props => props.imgUrl}");
  background-position: center center;
  background-size: cover;
`;

const GET_HOSPITAL_RECORD = gql`
  query getHospitalRecord($id: uuid!) {
    record: hkdutest_hospitals_by_pk(id: $id) {
      id
      name
      cover {
        name
        link
      }
      email
      fax
      phone
      address
      address_geo
      content
      website
      public
      emergency_service
    }
  }
`;

const Content = ({ id }) => {
  const handleMarkerClick = useCallback(url => {
    window.open(url);
  }, []);

  return (
    <Query query={GET_HOSPITAL_RECORD} variables={{ id }}>
      {({ data, loading }) => {
        if (!loading && !R.prop('record', data)) {
          return <PageNotFound />;
        }

        const record = R.propOr(0, 'record', data);
        const title = R.propOr('', 'name', record);
        const photo = R.pathOr('', ['cover', 'link'], record);
        const lat = R.pathOr('', ['address_geo', 'lat'], record);
        const lng = R.pathOr('', ['address_geo', 'lng'], record);

        return (
          <Section>
            <Helmet>
              <title>{title} - HKDU Public</title>
            </Helmet>
            <BreadcrumbWrapper>
              <Breadcrumb>
                <Breadcrumb.Item linkProps={{ as: Link, to: '/public/hospital' }}>Hospital</Breadcrumb.Item>
                <Breadcrumb.Item>{title}</Breadcrumb.Item>
              </Breadcrumb>
            </BreadcrumbWrapper>
            <Box mt={[7, 8]} mx={[5, 0]}>
              {photo && <Logo imgUrl={photo} />}
              <Title mt={6} mb={5}>
                {title}
              </Title>
              <Box pb={8}>
                <Field label="Type" value={record.public} render={value => (value ? 'Public' : 'Private')} />
                <Field
                  label="Emergency Service"
                  value={record.emergency_service}
                  render={value => (value ? 'Available' : 'Not Available')}
                />
              </Box>
              <Box pb={8}>
                <RowTitle>Contact Information</RowTitle>
                <Field
                  label="E-mail"
                  value={record.email}
                  render={value => <RowLink href={`mailto:${value}`}>{value}</RowLink>}
                />
                <Field label="Tel." value={record.phone} />
                <Field label="Fax" value={record.fax} />
                <Field
                  label="Address"
                  value={record.address}
                  render={value => (
                    <>
                      <RowLink href={composeGoogleMapUrl(value)} target="_blank" rel="noopener">
                        {value}
                      </RowLink>
                      <Map
                        markers={[
                          {
                            lat,
                            lng,
                          },
                        ]}
                        containerElement={<RowMap />}
                        onMarkerClick={() => {
                          handleMarkerClick(composeGoogleMapUrl(value));
                        }}
                      />
                    </>
                  )}
                />
                <Field
                  label="Website"
                  value={record.website}
                  render={value => (
                    <RowLink href={value} target="_blank" rel="noopener">
                      {value}
                    </RowLink>
                  )}
                />
              </Box>
              {record.content && (
                <Box pb={8}>
                  <HTMLContent html={record.content} />
                </Box>
              )}
            </Box>
          </Section>
        );
      }}
    </Query>
  );
};

export default Content;
